import React from 'react';
import classNames from 'classnames';

import { LinkedLogo } from '../../../components';
import { Text } from '../../PageBuilder/Primitives/Text/Text';
import { Ul, Li } from '../../PageBuilder/Primitives/List/List';
import { Link } from '../../PageBuilder/Primitives/Link/Link';

import containerCss from '../../PageBuilder/SectionBuilder/SectionContainer/SectionContainer.module.css';
import blockContainerCss from '../../PageBuilder/BlockBuilder/BlockContainer/BlockContainer.module.css';
import blockCss from '../../PageBuilder/BlockBuilder/BlockFooter/BlockFooter.module.css';
import css from '../../PageBuilder/SectionBuilder/SectionFooter/SectionFooter.module.css';

const COLUMN_CSS = [
  [css.contentCol1, css.gridCol1],
  [css.contentCol2, css.gridCol2],
  [css.contentCol3, css.gridCol3],
  [css.contentCol4, css.gridCol4],
];
const MAX_MOBILE_SCREEN_WIDTH = 1024;
const LINK_ITEM = /^[-*+]\s+\[([^\]]+)\]\(((?:\/|#|https?:\/\/|mailto:|tel:)[^)\s]*)\)$/;

// The links in a footer block whose markdown is nothing but a list of links, otherwise null.
const linkList = text => {
  const lines =
    text?.fieldType === 'markdown'
      ? (text.content || '')
          .split('\n')
          .map(line => line.trim())
          .filter(Boolean)
      : [];
  const matches = lines.map(line => line.match(LINK_ITEM));
  return matches.length > 0 && matches.every(Boolean)
    ? matches.map(([, label, href]) => ({ label, href }))
    : null;
};

const isPlainText = field => !field || field.fieldType === 'text';

/**
 * Whether LightFooter renders this footer asset the way PageBuilder's SectionFooter would:
 * no social media links or custom appearance, plain-text slogan and copyright, and blocks
 * that are only lists of links.
 *
 * @param {Object} footer the footer asset from the Console
 * @returns {boolean}
 */
export const fitsLightFooter = footer =>
  !footer.socialMediaLinks?.length &&
  footer.appearance?.fieldType !== 'customAppearance' &&
  isPlainText(footer.slogan) &&
  isPlainText(footer.copyright) &&
  (footer.blocks || []).every(block => block.blockType === 'footerBlock' && linkList(block.text));

const TextField = ({ field, className }) =>
  field?.content ? <Text className={className}>{field.content}</Text> : null;

/**
 * SectionFooter's markup and styles for a footer that fitsLightFooter, without PageBuilder's
 * markdown stack.
 *
 * @component
 * @param {Object} props the footer asset, plus linkLogoToExternalSite
 * @returns {JSX.Element} footer
 */
const LightFooter = props => {
  const { numberOfColumns = 1, slogan, copyright, blocks = [], linkLogoToExternalSite } = props;
  const [contentCss, gridCss] = COLUMN_CSS[numberOfColumns - 1] || COLUMN_CSS[0];
  const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
  const isMobileLayout = hasMatchMedia
    ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
    : true;

  return (
    <footer className={classNames(containerCss.root, css.root)} id="footer">
      <div className={classNames(containerCss.sectionContent, css.footerSectionContent)}>
        <div className={css.footer}>
          <div className={classNames(css.content, contentCss)}>
            <div>
              <LinkedLogo
                rootClassName={css.logoLink}
                logoClassName={css.logoWrapper}
                logoImageClassName={css.logoImage}
                linkToExternalSite={linkLogoToExternalSite}
                layout={isMobileLayout ? 'mobile' : 'desktop'}
              />
            </div>
            <div className={css.sloganMobile}>
              <TextField field={slogan} className={css.slogan} />
            </div>
            <div className={css.detailsInfo}>
              <div className={css.sloganDesktop}>
                <TextField field={slogan} className={css.slogan} />
              </div>
              <TextField field={copyright} className={css.copyright} />
            </div>
            <div className={classNames(css.grid, gridCss)}>
              {blocks.map(({ blockId, text }, i) => (
                <div
                  key={blockId || i}
                  className={classNames(blockContainerCss.root, blockCss.root)}
                  id={blockId}
                >
                  <nav className={blockCss.text} aria-label={blockId || 'Footer navigation'}>
                    <Ul>
                      {linkList(text).map(({ label, href }) => (
                        <Li key={`${href}-${label}`}>
                          <Link href={href}>{label}</Link>
                        </Li>
                      ))}
                    </Ul>
                  </nav>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default LightFooter;
